import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, exhaustMap, mergeMap, withLatestFrom } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { EMPTY, of } from 'rxjs';
import { FaxeStore } from '@app/store/faxe/faxe-store-model';
import {
  addManyFenceResult,
  fetchAllFenceResult,
  getFenceResult, postFenceResult
} from '@app/store/faxe/fence-result/fence-result.actions';
import { onOperationStatus } from '@app/store/client-slices/operation/operation.actions';
import { OperationStatusEnum } from '@app/store/client-slices/operation/model/operationstatusenum';
import { formatDateUTC, push } from '@app/fremework/functions';
import { SliceState } from '@app/store/faxe/sapiens-entity-state';
import { FenceResultsService } from '@app/rest-client/faxe/services/fence-results.service';
import { selectFenceResultsState } from './fence-result.selectos';
import { notEmptyByRaceSliceState } from '@app/domain/functions';

@Injectable()
export class FenceResultEffects {

  fetchFenceResultEffect = createEffect(() => this.actions$
    .pipe(
      ofType(fetchAllFenceResult),
      mergeMap(action =>
        this.store.select(selectFenceResultsState())
          .pipe(
            withLatestFrom(of(action)),
            mergeMap(([state, action]) => {

              if (notEmptyByRaceSliceState(
                state.fenceResults,
                action.raceGuid,
                action.requiredSliceState,
                state.sliceState)) {
                return EMPTY;
              }

              const actions = [];
              actions.push(getFenceResult({
                raceGuid: action.raceGuid,
                requiredSliceState: action.requiredSliceState,
                issuerStack: push(action.issuerStack, 'fetchFenceResultEffect')
              }));
              return of(...actions);
            })
          )
      ),
      catchError(() => EMPTY)
    )
  );

  getFenceResultEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(getFenceResult),
      exhaustMap((action) =>
        this.service.fenceResultsOnlyReportable({raceGuid: action.raceGuid, onlyReportable: action.requiredSliceState !== SliceState.total})
          .pipe(mergeMap((response) => {
              const actions = [];
              actions.push(addManyFenceResult(
                {
                  models: response.map(fenceResult => ({ ...fenceResult, raceGuid: action.raceGuid })),
                  sliceState: action.requiredSliceState,
                  issuerStack: push(action.issuerStack, `getFenceResultEffect`)
                }));
              return of(...actions);
            }),
            catchError((e) =>
              of(onOperationStatus({
                  model: {
                    status: e.status,
                    operationId: 'getFenceResultEffect',
                    callStack: push(action.issuerStack, 'getFenceResultEffect')
                  }
                })
              )
            )
          )),
      catchError(() => EMPTY)
    )
  );

  postFenceResultEffect = createEffect(() =>
    this.actions$.pipe(
      ofType(postFenceResult),
      mergeMap((action) => this.service
        .fenceResultPost({
          raceGuid: action.raceGuid,
          timestamp: formatDateUTC(new Date()),
          body: action.fenceResult,
        })
        .pipe(mergeMap(() => {
            const actions = [];
            actions.push(onOperationStatus({
              model: {
                status: OperationStatusEnum.ok,
                operationId: 'postFenceResult',
                correlationId: action.correlationId,
                callStack: push(action.issuerStack, 'postFenceResult')
              }
            }));
            return of(...actions);
          }),
          catchError((e) =>
            of(onOperationStatus({
                model: {
                  status: e.status,
                  operationId: 'postFenceResult',
                  callStack: push(action.issuerStack, 'postFenceResult')
                }
              })
            )
          )
        )),
      catchError(() => EMPTY)
    )
  );

  constructor(
    private service: FenceResultsService,
    private actions$: Actions,
    private store: Store<FaxeStore>
  ) {
  }

}
